import { useAuth } from '@/lib/auth';
import { DOUBLE_BENEFIT_PATH, DOUBLE_BENEFIT_PRICE } from '@/constants/proTerms';
import { doubleBenefitOn, useRubProvider } from '@/lib/rubProvider';
import { hasPro5Offer, PRO5_PATH, pro5DefaultLabel } from '@/lib/proOffer';
import { STRIPE_PRIMARY } from '@/lib/visitorRegion';
import { stripeDoubleBenefitLabel, useStripeFirstMonth } from '@/lib/stripePromo';

/**
 * Состояние акции «ДВОЙНАЯ ВЫГОДА» для витрины.
 *
 * Правило живёт на сервере (backend/double_benefit.py) и приезжает готовым
 * в /auth/me полем double_benefit. Второй копии правила в JS быть не должно:
 * цену и право всё равно проверяет касса, и витрина обязана показывать ровно
 * то, что касса примет. Здесь только раскладка на состояния показа.
 *
 * Состояния:
 *   'loading' — данных ещё нет, не показываем ничего;
 *   'guest'   — не авторизован: зовём регистрироваться;
 *   'trial'   — право есть и бонусные дни есть: «499 + N дней в подарок»;
 *   'grace'   — право есть, триал кончился: «499 до <дата>»;
 *   'none'    — права нет, плашки не бывает.
 *
 * Покупательнице разовых (backend/first_month_credit.py) сервер кладёт в то
 * же поле её цену max(499 − S, 1) и готовую строку credit_line: «Твои S ₽
 * уже учтены — ПРО за X ₽ + N дней в подарок». credit > 0 — показываем её.
 */
export function useDoubleBenefit() {
  const { user, isAuthenticated, loading } = useAuth();
  // Касса рублей с бэкенда: откат ру на Робокассу снимает акцию без пересборки.
  useRubProvider();
  // Stripe-путь (иностранец на ру): акция та же, цена — в его валюте
  // (lib/stripePromo.js); обычная цена для зачёркнутой — с сервера.
  const stripePath = STRIPE_PRIMARY;
  const stripeLabel = stripePath ? stripeDoubleBenefitLabel() : null;
  const stripeMonth = useStripeFirstMonth(
    stripePath && !!stripeLabel && isAuthenticated && !!(user && user.double_benefit && user.double_benefit.eligible));

  if (loading) {
    return { state: 'loading', eligible: false };
  }

  if (!isAuthenticated) {
    return { state: 'guest', eligible: false };
  }

  // Акции нет на Stripe-пути (иностранец на ру) и при Робокассе — права нет
  // ни у кого, что бы ни пришло с сервера (lib/rubProvider.js doubleBenefitOn).
  // С активным ПРО предложения первого месяца нет (сервер /auth/me снимает
  // право так же; здесь — на случай старого ответа из кеша).
  const db = user?.double_benefit;
  const activePro = Number(user?.effective_tier) >= 3;
  const on = stripePath ? !!stripeLabel : doubleBenefitOn();
  if (!on || !db || !db.eligible || activePro) {
    return { state: 'none', eligible: false };
  }

  const bonusDays = Number(db.bonus_days) || 0;

  return {
    // Делим по бонусным дням, а не по номеру дня: на границе триала день
    // может быть ещё 14-м, а дарить уже нечего — и «+0 дней в подарок»
    // выглядело бы насмешкой.
    state: bonusDays > 0 ? 'trial' : 'grace',
    eligible: true,
    day: db.day,
    bonusDays,
    daysLeftTrial: Number(db.days_left_trial) || 0,
    deadlineDate: db.deadline_date || null,
    plan: db.plan,
    price: db.price,
    basePrice: db.base_price,
    // Готовые подписи сумм: «499 ₽» / «999» (зачёркнутая) у рублей, «5,99 $» /
    // «9,99 $» на Stripe-пути. Зачёркнутой может не быть, пока цена не пришла.
    stripe: stripePath,
    priceLabel: stripePath ? stripeLabel : `${db.price} ₽`,
    basePriceLabel: stripePath ? ((stripeMonth && stripeMonth.regularLabel) || null) : String(db.base_price ?? ''),
    // Зачёт разовых: сколько учтено, готовая строка и откуда право
    // ('window' — дни 0–17, 'campaign' — личная ссылка письма на 3 дня).
    // На Stripe-пути их нет: строки рублёвые, а зачёт там — купоном
    // (ProCreditOffer, lib/stripePromo.js).
    credit: stripePath ? 0 : (Number(db.credit) || 0),
    creditLine: (!stripePath && Number(db.credit) > 0 && db.credit_line) || null,
    creditSource: stripePath ? null : (db.credit_source || null),
    // Акция по ссылке письма со своей надписью на оплате («Месяц правды»,
    // backend/truth499_offer.py): код и готовая строка.
    promo: stripePath ? null : (db.promo || null),
    promoLine: stripePath ? null : (db.promo_line || null),
  };
}

/**
 * Один вход в ПРО для всех замков: окон программ (AccessGuard), хорара,
 * натала и мобильного меню.
 *
 * Зачем один: у человека с правом на 499 «Открыть ПРО» вело на витрину
 * тарифов, где 999 — и акция, обещанная в кабинете и на главной, в замке
 * пропадала. Теперь замки спрашивают здесь, а правило по-прежнему считает
 * сервер (backend/double_benefit.py).
 *
 * ready=false — данных о праве ещё нет. Показать сначала «Открыть ПРО», а
 * через мгновение подменить его на акцию значит мигнуть ценой, поэтому
 * вызывающий либо ждёт, либо рисует нейтральный вариант.
 *
 * ГОСТЬ купить ПРО напрямую не может (решение владельца): любой его вход в
 * покупку ведёт сначала на регистрацию по одной почте. После неё человек —
 * триальщик, и ему уже положена ДВОЙНАЯ ВЫГОДА, поэтому возвращаем его на
 * витрину тарифов: там плашка акции и показывается. Признак isGuest отдаём
 * наружу — вызывающий может решить вообще ничего гостю не показывать.
 */
export const PRO_CTA_PLAIN = {
  label: 'Открыть ПРО',
  path: '/pricing',
  isDoubleBenefit: false,
};

/** Куда отправлять гостя, который пошёл покупать ПРО. */
export const PRO_CTA_GUEST = {
  label: 'Открыть ПРО',
  path: '/auth/register?redirect=%2Fpricing',
  isDoubleBenefit: false,
  isGuest: true,
};

export function useProCta() {
  const db = useDoubleBenefit();
  const { user } = useAuth();
  if (db.state === 'loading') return { ...PRO_CTA_PLAIN, ready: false };
  if (db.state === 'guest') return { ...PRO_CTA_GUEST, ready: true };
  // Цена из письма pro5_ru (ру): право на «первый месяц за 5 $» записано
  // личной ссылкой и ещё действует — любой вход в ПРО ведёт в ту же оплату
  // Stripe, что главная кнопка письма. isOffer — «у кнопки особая цена»
  // (499 или 5 $): вызывающие показывают по нему cta.label и cta.path.
  if (hasPro5Offer(user)) {
    return {
      ready: true,
      isDoubleBenefit: false,
      isPro5: true,
      isOffer: true,
      label: `Открыть ПРО за ${pro5DefaultLabel()}`,
      priceLabel: pro5DefaultLabel(),
      path: PRO5_PATH,
    };
  }
  if (!db.eligible) return { ...PRO_CTA_PLAIN, ready: true };
  // Покупательнице разовых — её цена с зачётом, той же строкой, что в
  // кабинете и письмах. Оплата — тот же план акции, сумму ставит сервер.
  if (db.creditLine) {
    return {
      ready: true,
      isDoubleBenefit: true,
      isOffer: true,
      isCredit: true,
      label: db.creditLine,
      path: DOUBLE_BENEFIT_PATH,
    };
  }
  return {
    ready: true,
    isDoubleBenefit: true,
    isOffer: true,
    // Цена — из ответа сервера; константа только на случай пустого поля.
    label: `ДВОЙНАЯ ВЫГОДА — ПРО за ${db.priceLabel || `${db.price || DOUBLE_BENEFIT_PRICE} ₽`}`,
    priceLabel: db.priceLabel || null,
    path: DOUBLE_BENEFIT_PATH,
  };
}

/** Подарок дня из серии писем — тексты приходят с сервера, не из JS. */
export function useGiftOfDay() {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading || !isAuthenticated) return null;
  return user?.gift_of_day || null;
}

export default useDoubleBenefit;
